import { API_BASE_URL } from '../config.js';

export type ClientErrorKind = 'uncaught_error' | 'unhandled_rejection' | 'msal_error';

/**
 * Resolves a bearer token for an error report, or `null` when the user is not
 * signed in. Implementations MUST be silent: never trigger an interactive
 * login and never throw — filing telemetry must not bounce the user to the IdP.
 */
export type ClientErrorTokenProvider = () => Promise<string | null>;

let tokenProvider: ClientErrorTokenProvider | null = null;

/**
 * Registered once at bootstrap (see index.tsx). Injected rather than imported
 * so this module stays dependency-free: `auth/msalConfig.ts` imports *this*
 * module to report ACQUIRE_TOKEN_FAILURE, so importing MSAL here would create
 * an import cycle.
 */
export function setClientErrorTokenProvider(provider: ClientErrorTokenProvider | null): void {
  tokenProvider = provider;
}

// Re-entrancy guard. A failing acquireTokenSilent() emits MSAL's
// ACQUIRE_TOKEN_FAILURE event, which calls reportClientError('msal_error'),
// which would acquire a token again — an unbounded loop. While a token is
// being resolved, further reports are dropped to the console instead.
let resolvingToken = false;

/**
 * Fire-and-forget report of a client-side error to the BFF's
 * POST /v1/client-errors endpoint, which re-emits it through the same
 * withRequestLog/OTel pipeline as the BFF's own logs. No OTLP credentials are
 * ever present in the browser.
 *
 * The endpoint is JWT-protected like every other BFF route (it is a write sink
 * on a public host), so **errors are only reported once the user is signed in**.
 * Anything raised before a token exists — including MSAL's own login failures —
 * goes to the browser console only. That is a deliberate trade: an
 * unauthenticated write endpoint was judged the greater risk. See
 * docs/kong/tps-kong-onboarding.md.
 *
 * Uses fetch + `keepalive` (survives page unload, like sendBeacon) — sendBeacon
 * itself cannot be used because it cannot set an Authorization header.
 */
export function reportClientError(kind: ClientErrorKind, message: string, stack?: string): void {
  void sendReport(kind, message, stack);
}

async function sendReport(kind: ClientErrorKind, message: string, stack?: string): Promise<void> {
  if (resolvingToken) {
    console.error('[client-error] dropped while resolving token', kind, message);
    return;
  }

  let token: string | null = null;
  if (tokenProvider) {
    resolvingToken = true;
    try {
      token = await tokenProvider();
    } catch {
      token = null;
    } finally {
      resolvingToken = false;
    }
  }

  if (!token) {
    // Pre-login (or signed-out) error: nothing to authenticate with, so it is
    // not reportable. Surface it locally so it is still debuggable.
    console.error('[client-error] not reported (no session)', kind, message, stack);
    return;
  }

  const payload = JSON.stringify({
    kind,
    message,
    stack,
    url: window.location.href,
    userAgent: navigator.userAgent,
  });

  try {
    await fetch(`${API_BASE_URL}/v1/client-errors`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: payload,
      keepalive: true,
    });
  } catch {
    // Best-effort — nothing more to do if this also fails.
  }
}
